import { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useNavigate } from 'react-router-dom';
import { getTechLocations, getCompanyLocations } from '../services/api';
import { TechLocation, CompanyLocation, MissingCompany } from '../services/types';
import 'leaflet/dist/leaflet.css';

const smallTechIcon = L.divIcon({
  className: 'custom-marker-sm',
  html: `<div style="width:24px;height:24px;border-radius:50%;background:#3b82f6;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.3);"></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12],
});

const smallCompanyIcon = L.divIcon({
  className: 'custom-marker-sm',
  html: `<div style="width:18px;height:18px;border-radius:50%;background:#22c55e;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.3);"></div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
  popupAnchor: [0, -9],
});

// Map dashboard filter to ticket status for API
const FILTER_TO_STATUS: Record<string, string | undefined> = {
  'new': 'New',
  'in-progress': 'In Progress',
  'waiting-customer': 'Waiting Customer',
  'waiting-us': 'Waiting On Us',
  'all': undefined, // all open statuses
};

function AutoFit({ techLocations, companyLocations }: {
  techLocations: TechLocation[];
  companyLocations: CompanyLocation[];
}) {
  const map = useMap();

  useEffect(() => {
    const points: [number, number][] = [
      ...techLocations.map((l) => [l.latitude, l.longitude] as [number, number]),
      ...companyLocations.map((l) => [l.latitude, l.longitude] as [number, number]),
    ];

    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 11);
    } else {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [techLocations, companyLocations, map]);

  return null;
}

interface FieldMapWidgetProps {
  filter?: string;
  companyTickets?: Map<number, number>;
}

export default function FieldMapWidget({ filter, companyTickets }: FieldMapWidgetProps) {
  const navigate = useNavigate();
  const [locations, setLocations] = useState<TechLocation[]>([]);
  const [companyLocs, setCompanyLocs] = useState<CompanyLocation[]>([]);
  const [missingAddresses, setMissingAddresses] = useState<MissingCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(true);

  const statusFilter = filter ? FILTER_TO_STATUS[filter] : undefined;
  // Stable key for companyTickets so useCallback doesn't re-fire on every render
  const companyTicketsKey = companyTickets ? Array.from(companyTickets.keys()).sort().join(',') : '';

  const loadLocations = useCallback(async () => {
    try {
      const [techData, companyResponse] = await Promise.all([
        getTechLocations(),
        getCompanyLocations(companyTickets ? undefined : statusFilter, companyTickets),
      ]);
      setLocations(techData);
      setCompanyLocs(companyResponse.data);
      setMissingAddresses(companyResponse.missingAddresses || []);
    } catch {
      // Silently fail on widget
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, companyTicketsKey]);

  useEffect(() => {
    loadLocations();
    const interval = setInterval(loadLocations, 60000);
    return () => clearInterval(interval);
  }, [loadLocations]);

  const defaultCenter: [number, number] = [33.4936, -117.1484];
  const hasData = locations.length > 0 || companyLocs.length > 0;

  // Label for the current filter
  const filterLabel = filter && filter !== 'all'
    ? FILTER_TO_STATUS[filter] || filter
    : undefined;

  return (
    <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden relative" style={{ zIndex: 0 }}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="text-xs font-medium text-white">Field Map</span>
          <span className="text-[10px] text-slate-500">
            {locations.length} tech{locations.length !== 1 ? 's' : ''}
            {companyLocs.length > 0 && `, ${companyLocs.length} client${companyLocs.length !== 1 ? 's' : ''}`}
            {filterLabel && <span className="text-slate-600"> ({filterLabel})</span>}
          </span>
          {missingAddresses.length > 0 && (
            <button
              onClick={() => navigate('/field-map')}
              className="text-[10px] text-amber-400 hover:text-amber-300 transition-colors"
              title={missingAddresses.map(m => m.companyName).join(', ')}
            >
              {missingAddresses.length} unmapped
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="text-[10px] text-slate-400 hover:text-slate-300 transition-colors"
            title={collapsed ? 'Expand map' : 'Collapse map'}
          >
            {collapsed ? 'expand' : 'collapse'}
          </button>
          <button
            onClick={() => navigate('/field-map')}
            className="text-[10px] text-blue-400 hover:text-blue-300 transition-colors"
          >
            Full Map
          </button>
        </div>
      </div>

      {!collapsed && (
        <div style={{ height: '200px' }}>
          {loading ? (
            <div className="flex items-center justify-center h-full bg-slate-900/50">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500" />
            </div>
          ) : !hasData ? (
            <div className="flex items-center justify-center h-full text-xs text-slate-500">
              No active locations
            </div>
          ) : (
            <MapContainer
              center={defaultCenter}
              zoom={10}
              style={{ height: '100%', width: '100%' }}
              zoomControl={false}
              attributionControl={false}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <AutoFit techLocations={locations} companyLocations={companyLocs} />

              {locations.map((loc) => (
                <Marker
                  key={`tech-${loc.resourceId}`}
                  position={[loc.latitude, loc.longitude]}
                  icon={smallTechIcon}
                >
                  <Popup>
                    <span className="text-xs font-medium">{loc.resourceName}</span>
                  </Popup>
                </Marker>
              ))}

              {companyLocs.map((loc) => (
                <Marker
                  key={`company-${loc.companyId}`}
                  position={[loc.latitude, loc.longitude]}
                  icon={smallCompanyIcon}
                >
                  <Tooltip direction="top" offset={[0, -9]} opacity={0.95}>
                    <div className="text-xs">
                      <div className="font-medium">{loc.companyName}</div>
                      <div className="text-slate-500">{loc.openTicketCount} open ticket{loc.openTicketCount !== 1 ? 's' : ''}</div>
                    </div>
                  </Tooltip>
                </Marker>
              ))}
            </MapContainer>
          )}
        </div>
      )}
    </div>
  );
}
